import { Component, OnInit, Input, Output, ContentChildren, ElementRef, EventEmitter, QueryList } from '@angular/core';
import { CfCoreComponent } from '../core/core.component';
import { FormGroup, FormControl } from '@angular/forms';
import { CfFormQuestionComponent } from '../form-question/form-question.component';
import { CfGroupQuestionComponent } from '../group-question/group-question.component';
import { FormQuestionControlService } from '../../services/form-generator/form-question-control.service';
import { ButtonModel } from '../../models/button/button.model';
import { FormModel } from '../../models/form/form.model';
import { FormStylingModel } from '../../models/form/form-styling.model';
import { FormTemplates } from '../../templates/form.template';
import { TemplateService } from '../../services/template-service/template.service';
/**
 * <p>CF Form Component</p>
 * <pre>
 * <code>
 * import {{'{'}} FormModel {{'}'}} from 'cedrus-fusion'
 * import {{'{'}} FormStylingModel {{'}'}} from 'cedrus-fusion'
 * <code><</code>cf-form<code>></code><code><</code><code>/</code>cf-form<code>></code>
 * </code>
 * </pre>
 */
export class CfFormComponent extends CfCoreComponent implements OnInit {
/**
 * <p>Example:</p>
 * <code><pre>{{'{'}}
 *  questions: this.myQuestions,
 *  key: 'person',
 *  payload: this.person,
 *  submitButton: {{'{'}} label: 'Save' {{'}'}}
 * {{'}'}}</pre></code>
 * <h4>questions: array</h4>
 * Array with questions.
 * <h4>key: string</h4>
 * Form key
 * <h4>payload: any</h4>
 * Data to be patched into form.
 * <h4>submitButton: ButtonModel</h4>
 * Form submit button
 */
properties: FormModel;
/**
 * <p>Styling object:</p>
 * <h4>container: StylingModel</h4>
 * Styling properties for main container
 * <h4>form: StylingModel</h4>
 * Styling for the form
 * <h4>table: StylingModel</h4>
 * Styling for the table with questions
 * <h4>content: StylingModel</h4>
 * Styling for the form content
 * <h4>submitSection: StylingModel</h4>
 * Styling for the form submit section
 * <h4>submitButton: ButtonStylingModel</h4>
 * Styling for submit button
 */
styling: FormStylingModel;
/**
 * Array with questions
 * @param {?} value
 * @return {?}
 */
set questions(value: any[]) { this.properties.questions = value; }
/**
 * Questions from the template to be added to the questions sent from the user
 * @param {?} value
 * @return {?}
 */
set commonQuestions(value: any[]) { this.properties.commonQuestions = value; }
/**
 * Name of form group with questions
 * @param {?} value
 * @return {?}
 */
set key(value: string) { this.properties.key = value; }
/**
 * Data to be patched into form
 * @param {?} value
 * @return {?}
 */
set payload(value: any) { this.properties.payload = value; }
/**
 * Form submit button
 * @param {?} value
 * @return {?}
 */
set submitButton(value: ButtonModel) { this.properties.submitButton = value; }
/**
 * @hidden
 */
form: FormGroup;
/**
 * @hidden
 */
formQuestions: QueryList<CfFormQuestionComponent>;
/**
 * @hidden
 */
groupQuestions: QueryList<CfGroupQuestionComponent>;
/**
 * Output event with form payload
 */
onSubmit = new EventEmitter();
/**
 * @hidden
 * @param {?} elementRef
 * @param {?} templateService
 * @param {?} fqcs
 */
constructor(public elementRef: ElementRef, /**@hidden */ templateService: TemplateService,
private fqcs: FormQuestionControlService) {
    super(elementRef, templateService);
    this.properties = new FormModel();
    this.styling = new FormStylingModel();
  }
/**
 * @hidden
 * @return {?}
 */
ngOnInit() {
    super.ngOnInit();
    this.getMyTemplate('form', FormTemplates).then(() => {
      this.mergeProperties(this.activeTemplate.property, this.properties, 'FORM').then(sampler => {
        if (this.properties.questions.length > 0) {
          // Add the common questions from the template to the questions sent from the user
          if (this.properties.commonQuestions.length !== 0) {
            this.properties.questions.splice(0, 0, {key: 'commonForm', questions: this.properties.commonQuestions});
          }
          // Create the form validation procedure the from the questions object
          this.form = this.fqcs.toFormGroup(this.properties.questions);
          if (this.properties.payload !== null) { this.form.patchValue(this.properties.payload); }
        }
        this.styling = Object.assign({}, this.activeTemplate.style, this.styling);
        // console.log('FORM', this);
      });
    });
  }
/**
 * Method to submit form value and emit it
 * @return {?}
 */
onPressSubmit() {
    this.properties.payload = JSON.stringify(this.form.value);
    this.onSubmit.emit(this.properties.payload);
    return this.properties.payload;
  }
/**
 * @hidden
 * @return {?}
 */
ngAfterContentInit() {
    if ((this.formQuestions != null && this.formQuestions.length > 0) || (this.groupQuestions != null && this.groupQuestions.length > 0)) {
      let /** @type {?} */ manualQuestions = [];
      if (this.formQuestions != null && this.formQuestions.length > 0) {
        this.formQuestions.toArray().forEach((control, index) => {
          manualQuestions.push(control.properties);
        });
      }
      this.form = this.fqcs.toFormGroup(manualQuestions);
      if (this.groupQuestions != null && this.groupQuestions.length > 0) {
        this.groupQuestions.toArray().forEach((group, index) => {
          if (group.properties.key == null) { group.properties.key = 'group' + index; }
          this.form.addControl(group.properties.key, group.form);
        });
        this.groupQuestions.toArray().forEach((group, index) => {
          group.form = this.form.controls[group.properties.key];
        });
      }
      if (this.formQuestions != null && this.formQuestions.length > 0) {
        this.formQuestions.toArray().forEach((control, index) => control.form = this.form);
      }
      this.populatePayload()
    }
  }
/**
 * @hidden
 * @return {?}
 */
populatePayload() {
    if (this.properties.payload != null) { this.form.patchValue(this.properties.payload); }
  }
/**
 * @hidden
 * @param {?} changes
 * @return {?}
 */
ngOnChanges(changes: any) {
    if (this.properties.payload != null) {
      if (changes.payload != null && this.form != null) {
        this.form.patchValue(changes.payload.currentValue);
      }
    }
  }
static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
  selector: 'cf-form',
  template: "<div *ngIf=\"_inited && properties?.display\" class=\"cf-form-container {{styling?.container?.class}}\" [ngClass]=\"getDynamicClasses(styling?.container?.dynamicClass)\"> <form (ngSubmit)=\"onPressSubmit()\" [formGroup]=\"form\" class=\"cf-form {{styling?.form?.class}}\" [ngClass]=\"getDynamicClasses(styling?.form?.dynamicClass)\"> <table class=\"cf-form-table {{styling?.table?.class}}\" [ngClass]=\"getDynamicClasses(styling?.table?.dynamicClass)\"> <ng-template let-question ngFor [ngForOf]=\"properties.questions\"> <tr *ngIf=\"question?.questions\"> <cf-group-question [groupQuestion]=\"question\" [form]=\"form\"></cf-group-question> </tr> <tr *ngIf=\"question?.questions==null\"> <cf-form-question [question]=\"question\" [form]=\"form\"></cf-form-question> </tr> </ng-template> </table> <div class=\"cf-form-content {{styling?.content?.class}}\" [ngClass]=\"getDynamicClasses(styling?.content?.dynamicClass)\"><ng-content></ng-content></div>     <div *ngIf=\"properties.submitButton?.display\" class=\"cf-form-submit-section {{styling?.submitSection?.class}}\" [ngClass]=\"getDynamicClasses(styling?.submitSection?.dynamicClass)\"> <cf-button type=\"submit\" [disable]=\"!form.valid\" [properties]=\"properties.submitButton\" [styling]=\"styling?.submitButton\"></cf-button> </div> </form> </div>",
  styles: ["/* cellspacing */ table.cf-form-table { width: 100%; border-collapse: separate; border-spacing: 10px; } .cf-form-submit-section { display: flex; justify-content: flex-end; margin-top: 10px; } "],
  providers: [FormQuestionControlService]
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: ElementRef, },
{type: TemplateService, },
{type: FormQuestionControlService, },
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'properties': [{ type: Input },],
'styling': [{ type: Input },],
'questions': [{ type: Input, args: ['questions', ] },],
'commonQuestions': [{ type: Input, args: ['commonQuestions', ] },],
'key': [{ type: Input, args: ['key', ] },],
'payload': [{ type: Input, args: ['payload', ] },],
'submitButton': [{ type: Input, args: ['submitButton', ] },],
'formQuestions': [{ type: ContentChildren, args: [CfFormQuestionComponent, ] },],
'groupQuestions': [{ type: ContentChildren, args: [CfGroupQuestionComponent, ] },],
'onSubmit': [{ type: Output },],
};
}

function CfFormComponent_tsickle_Closure_declarations() {
/** @type {?} */
CfFormComponent.decorators;
/**
 * @nocollapse
 * @type {?}
 */
CfFormComponent.ctorParameters;
/** @type {?} */
CfFormComponent.propDecorators;
/**
 * <p>Example:</p>
 * <code><pre>{{'{'}}
 *  questions: this.myQuestions,
 *  key: 'person',
 *  payload: this.person,
 *  submitButton: {{'{'}} label: 'Save' {{'}'}}
 * {{'}'}}</pre></code>
 * <h4>questions: array</h4>
 * Array with questions.
 * <h4>key: string</h4>
 * Form key
 * <h4>payload: any</h4>
 * Data to be patched into form.
 * <h4>submitButton: ButtonModel</h4>
 * Form submit button
 * @type {?}
 */
CfFormComponent.prototype.properties;
/**
 * <p>Styling object:</p>
 * <h4>container: StylingModel</h4>
 * Styling properties for main container
 * <h4>form: StylingModel</h4>
 * Styling for the form
 * <h4>table: StylingModel</h4>
 * Styling for the table with questions
 * <h4>content: StylingModel</h4>
 * Styling for the form content
 * <h4>submitSection: StylingModel</h4>
 * Styling for the form submit section
 * <h4>submitButton: ButtonStylingModel</h4>
 * Styling for submit button
 * @type {?}
 */
CfFormComponent.prototype.styling;
/**
 * @hidden
 * @type {?}
 */
CfFormComponent.prototype.form;
/**
 * @hidden
 * @type {?}
 */
CfFormComponent.prototype.formQuestions;
/**
 * @hidden
 * @type {?}
 */
CfFormComponent.prototype.groupQuestions;
/**
 * Output event with form payload
 * @type {?}
 */
CfFormComponent.prototype.onSubmit;
/** @type {?} */
CfFormComponent.prototype.elementRef;
/** @type {?} */
CfFormComponent.prototype.fqcs;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
